import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    // The trigger's aria-expanded drives the icon, the content's inert drives
    // whether the panel is visible. The cart drawer re-renders on every cart
    // change, and a re-render can update one without the other, which leaves a
    // minus icon above an empty panel. Keep the panel in step with the trigger.
    this.#syncDisclosure();

    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    if (trigger && content && typeof MutationObserver !== 'undefined') {
      this.#observer = new MutationObserver(this.#syncDisclosure);
      // Watch both sides: a re-render can reset either one on its own.
      this.#observer.observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });
      this.#observer.observe(content, { attributes: true, attributeFilter: ['inert'] });
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    super.disconnectedCallback();
  }

  /** Content follows the trigger, whatever else touched the markup. */
  #syncDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    if (!trigger || !content) return;

    const expanded = trigger.matches('[aria-expanded="true"]');
    if (content.inert === expanded) content.inert = !expanded;
  };

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.inert = expanded;
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
